import { ChevronRight, MapPin, X } from 'lucide-react';
import { ChargingLocation } from '../../types';

/**
 * "You're at X" prompt, shown on the map when the device reports a confident fix
 * within a few dozen metres of a station.
 *
 * Deliberately a prompt rather than an automatic navigation: proximity is an
 * inference, and the interface should not assert more certainty than the signal
 * carries. It is also most likely to be wrong exactly when it would be most
 * unwelcome — someone parked at a station who opened the app to check a balance.
 */

interface NearbyLocationPromptProps {
  location: ChargingLocation;
  onView: () => void;
  onDismiss: () => void;
}

export function NearbyLocationPrompt({
  location,
  onView,
  onDismiss,
}: NearbyLocationPromptProps) {
  return (
    <div className="absolute top-full left-0 right-0 mt-3 z-[999]">
      <div
        role="button"
        tabIndex={0}
        onClick={onView}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onView();
          }
        }}
        aria-label={`View ${location.name}`}
        className="flex cursor-pointer items-start gap-3 rounded-2xl bg-white dark:bg-gray-800 px-4 py-3 shadow-lg border border-gray-100 dark:border-gray-700 transition-colors active:bg-gray-50 dark:active:bg-gray-700/50"
      >
        <div className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#8bc43e]/15">
          <MapPin className="h-5 w-5 text-[#8bc43e]" />
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-500 dark:text-gray-400">You&rsquo;re at</p>
          <p className="text-sm font-semibold text-gray-900 dark:text-white break-words line-clamp-2">
            {location.name}
          </p>
        </div>

        <div
          aria-hidden="true"
          className="flex h-8 w-8 flex-none self-center items-center justify-center rounded-full bg-[#8bc43e]/15 text-[#8bc43e]"
        >
          <ChevronRight className="h-5 w-5" />
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          aria-label="Dismiss"
          className="flex h-7 w-7 flex-none items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-500"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
